import { Inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ContractInfo, ParkInfo, ServiceInfo, ServiceInfoWithGroup, SpaqFireDb } from '@shared/core';
import { select, Store } from '@ngrx/store';
import { from, Observable, of, zip } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
	getAccessCodeData,
	getContractCreateSelectedPayment,
	getContractData,
	getContractDataGroupId,
	getLayoutScopeId,
	getOrganizationEntities
} from '../selectors';
import { getCustomPickupLocations } from '../custom-selectors';
import { ApplicationInsightsService, StoryType } from './application-insights';
import { CONTRACT_CONDITIONS_TEXT, ContractConditionsText, SERVICE_MODEL, ServiceModelCtor } from './contract.tokens';

export const createService = (service: ServiceInfo, ServiceCtor: ServiceModelCtor): any => {
	const s = new ServiceCtor(service);
	const invoiceAmount = s.partialPrice;
	const invoiceFrom = s.startDate.toISOString();
	const endDate = s.partialEndDate.toISOString();
	const invoiceTo = s.partialInvoiceToDate.toISOString();
	let approved = false;

	if (service.serviceType.toString() === 'Public' || service.serviceType.toString() === 'Private') {
		approved = true;
	}

	return {
		serviceId: service.availableServiceId,
		serviceDescription: service.serviceDescription,
		isApproved: approved,
		effectiveDate: invoiceFrom,
		effectiveDate2: invoiceFrom,
		endDate: endDate,
		isPaidByGroup: false,
		invoiceAmount: invoiceAmount.toString(),
		invoiceFrom: invoiceFrom,
		invoiceTo: invoiceTo
	};
};

@Injectable()
export class ContractService {
	constructor(
		private db: SpaqFireDb,
		private auth: AngularFireAuth,
		private store: Store<any>,
		private appInsights: ApplicationInsightsService,
		@Inject(CONTRACT_CONDITIONS_TEXT) private conditionsText: ContractConditionsText,
		@Inject(SERVICE_MODEL) private ServiceModel: ServiceModelCtor
	) {}

	acceptConditionsPayment(isCheck = true): Observable<void> {
		this.appInsights.addStory(StoryType.ContractWizard, 'acceptConditionsPayment() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					this.db.updateUserContract(u.uid, {
						step: 1,
						reminded: null,
						lastUpdated: new Date().toISOString(),
						conditionsPaymentAcceptedAt: !isCheck ? null : new Date().toISOString()
					})
				)
			)
		);
	}

	acceptConditionsService(isCheck = true): Observable<void> {
		const updatePayload = {
			step: 1,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			conditionsServiceAcceptedAt: !isCheck ? null : new Date().toISOString()
		};

		this.appInsights.addStory(StoryType.ContractWizard, 'acceptConditionsService() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u => from(this.db.updateUserContract(u.uid, updatePayload)))
		);
	}

	acceptConditionsServiceApprobationPayment(isCheck): Observable<void> {
		const updatePayload = {
			step: 1,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			paymentApprobationAcceptedAt: !isCheck ? null : new Date().toISOString()
		};

		this.appInsights.addStory(StoryType.ContractWizard, 'acceptConditionsServiceApprobationPayment() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u => from(this.db.updateUserContract(u.uid, updatePayload)))
		);
	}
	acceptConditionsPlaceReservation(isCheck): Observable<void> {
		const updatePayload = {
			step: 1,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			placesReservationAcceptedAt: !isCheck ? null : new Date().toISOString()
		};
		this.appInsights.addStory(StoryType.ContractWizard, 'acceptConditionsPlaceReservation() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u => from(this.db.updateUserContract(u.uid, updatePayload)))
		);
	}
	agreeToMove(isCheck): Observable<void> {
		const updatePayload = {
			step: 1,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			agreeToMove: !isCheck ? null : true
		};
		this.appInsights.addStory(StoryType.ContractWizard, 'agreeToMove() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u => from(this.db.updateUserContract(u.uid, updatePayload)))
		);
	}

	resetExpiration(): Observable<void> {
		const updatePayload = {
			step: 1,
			lastUpdated: new Date().toISOString(),
			service: null,
			setServiceAsNull: 'resetExpiration',
			resetExpiration: true,
			serviceAddedAt: null,
			subscriptionService: null
		};
		this.appInsights.addStory(StoryType.ContractWizard, 'resetExpiration() (nulls service/subscriptionService)');
		return this.auth.user.pipe(
			take(1),
			switchMap(u => from(this.db.updateUserContract(u.uid, updatePayload)))
		);
	}

	addAccessCard(accessCard): Observable<any> {
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.setUserSubscriptionAccessCardNumber(u.uid, accessCard),
					this.db.setUserContractAccessCardNumber(u.uid, accessCard)
				)
			)
		);
	}

	haveAccessCard(accessCard): Observable<any> {
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.setUserContractHaveAccessCard(u.uid, accessCard),
					this.db.setUserSubscriptionAccessCardNumber(u.uid, null),
					this.db.setUserContractAccessCardNumber(u.uid, null)
				)
			)
		);
	}

	acceptWaitList(isCheck): Observable<any> {
		const updatePayload = {
			step: 1,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			enableWaitingList: !isCheck ? null : true
		};

		this.appInsights.addStory(StoryType.ContractWizard, 'acceptWaitList() called', { isCheck });
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.updateUserContract(u.uid, updatePayload),
					this.db.setUserSubscriptionWaitingList(u.uid, {
						key: (isCheck && '1') || '0',
						value: (isCheck && 'Oui') || 'Non'
					}),
					this.db.setUserContractWaitingList(u.uid, {
						key: (isCheck && '1') || '0',
						value: (isCheck && 'Oui') || 'Non'
					})
				)
			)
		);
	}

	acceptConditionsServicePayment(isCheck, payment = null): Observable<void> {
		this.appInsights.addStory(StoryType.ContractWizard, 'acceptConditionsServicePayment() called', { isCheck, payment });
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					this.db.updateUserContract(u.uid, {
						isPaid: isCheck && payment === 'Cash',
						isPaid5: isCheck && payment === 'Cash',
						...(isCheck && payment === 'Cash'
							? { setPaidAstrue: 'acceptConditionsServicePayment' }
							: { setPaidAsFalse: 'acceptConditionsServicePayment' }),
						step: 1,
						reminded: null,
						lastUpdated: new Date().toISOString(),
						paymentAcceptedAt: !isCheck ? null : new Date().toISOString()
					})
				)
			)
		);
	}

	addPark(park: ParkInfo): Observable<any> {
		this.appInsights.addStory(StoryType.ContractWizard, 'addPark() (nulls groupId/sectorId/service/subscriptionService)', {
			parkIdentificationNo: park?.parkIdentificationNo
		});
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.updateUserContract(u.uid, {
						step: 1,
						reminded: null,
						groupId: null,
						sectorId: null,
						lastUpdated: new Date().toISOString(),
						park,
						service: null,
						setServiceAsNull: 'addPark',
						paymentBeforeApproval: null,
						payment: null,
						subscriptionService: null,
						conditionsServiceAcceptedAt: null,
						conditionsPaymentAcceptedAt: null,
						addPark: true
					}),

					this.db.setUserSubscriptionCustom(u.uid, null)
				)
			)
		);
	}

	addPayment(payment: any): Observable<void> {
		this.appInsights.addStory(StoryType.ContractWizard, 'addPayment() called', { payment });
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					this.db.updateUserContract(u.uid, {
						payment,
						conditionsPaymentAcceptedAt: null,
						paymentAcceptedAt: null
					})
				)
			)
		);
	}

	deleteService() {
		const serviceData = {
			step: 1,
			service: null,
			setServiceAsNull: 'deleteService',
			conditionsServiceAcceptedAt: null,
			deleteService: true,
			deleteService2: true,
			lastUpdated: new Date().toISOString(),
			subscriptionService: null,
			paymentBeforeApproval: null
		};

		this.appInsights.addStory(StoryType.ContractWizard, 'deleteService() (nulls service/subscriptionService)');
		return this.auth.user.pipe(
			take(1),
			switchMap(u => this.db.updateUserContract(u.uid, serviceData))
		);
	}

	addGroup(groupId: any): Observable<any> {
		this.appInsights.addStory(StoryType.ContractWizard, 'addGroup() (resets sectorId, nulls subscriptionService)', {
			groupId: groupId?.groupId ?? groupId
		});
		return this.auth.user.pipe(
			take(1),
			withLatestFrom(this.store.pipe(select(getLayoutScopeId))),
			switchMap(([u, scopeId]: [any, string]) => [
				this.db.updateUserContract(u.uid, {
					groupId: groupId,
					sectorId: null,
					enableWaitingList: null,
					service: null,
					setServiceAsNull: 'addGroup'
				}),
				this.db.updateUserAccessCode(u.uid, {
					groupCode: (groupId && groupId.groupCode) || groupId,
					organizationCode: scopeId
				})
			])
		);
	}

	addSector(sectorId: any): Observable<any> {
		this.appInsights.addStory(StoryType.ContractWizard, 'addSector() (nulls service, sets sectorId)', {
			sectorId: sectorId?.id ?? sectorId
		});
		return this.auth.user.pipe(
			take(1),
			withLatestFrom(
				this.store.pipe(
					select(getCustomPickupLocations),
					map((x: any) => (x && x[sectorId.id]) || null)
				)
			),
			switchMap(([u, meta]: any) =>
				from([
					this.db.updateUserContract(u.uid, {
						sectorId: sectorId,
						service: null,
						setServiceAsNull: 'addSector',
						enableWaitingList: null
					}),
					this.db.setUserSubscriptionPickupLocation(u.uid, meta || null),
					this.db.setUserContractPickupLocation(u.uid, meta || null)
				])
			)
		);
	}

	addPickupLocation(pickupLocation) {
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from([
					this.db.setUserSubscriptionPickupLocation(u.uid, pickupLocation || null),
					this.db.setUserContractPickupLocation(u.uid, pickupLocation || null)
				])
			)
		);
	}

	setServiceSelected(service: any): Observable<void> {
		this.appInsights.addStory(StoryType.ContractWizard, 'setServiceSelected() called', {
			serviceId: service?.availableServiceId
		});
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					this.db.updateUserContract(u.uid, {
						selectedService: service.availableServiceId,
						payment: (service.serviceBillingType === 'DAS' && 'DAS') || null
					})
				)
			)
		);
	}

	addService(service: ServiceInfoWithGroup | any) {
		const subscriptionService = createService(service.service, this.ServiceModel);
		const serviceData = {
			step: 2,
			cancelService: null,
			cancelDate: null,
			host: window.location.hostname + ':' + window.location.port,
			reminded: null,
			lastUpdated: new Date().toISOString(),
			service: service.service,
			groupId: service.groupId || null,
			agreeToMove: null,
			haveAccessCard: null,
			sectorId: service.sectorId || null,
			subscriptionService,
			isPaid: false,
			setPaidAsFalse: 'addService',
			paymentBeforeApproval: null,
			payment:
				((service.service.serviceBillingType === 'DAS' ||
					(service.service.isDasPaymentAccepted &&
						!service.service.isPpaPaymentAccepted &&
						!service.service.isFinancialInstitutionPaymentAccepted &&
						!service.service.isBankingCardPaymentAccepted)) &&
					'DAS') ||
				(!service.service.price && service.service.serviceBillingType !== 'AtBooking' && 'Cash') ||
				null,
			conditionsPaymentAcceptedAt: null,
			serviceAddedAt: new Date().toISOString()
		};

		this.appInsights.addStory(StoryType.ContractWizard, 'addService() (populates subscriptionService)', {
			serviceId: service?.service?.availableServiceId
		});
		return this.auth.user.pipe(
			take(1),
			switchMap(u => {
				let updateAccessCodeWhenGroupFound: any = of(null);
				if (service.groupId) {
					updateAccessCodeWhenGroupFound = from(
						this.db.updateUserAccessCode(u.uid, {
							userCode: (service.sectorId && service.sectorId.id) || null,
							organizationCode: service.scopeId || '',
							groupCode: (service.groupId && service.groupId.groupCode) || '',
							isScope: true
						})
					);
				}
				return zip(this.db.updateUserContract(u.uid, serviceData), updateAccessCodeWhenGroupFound);
			})
		);
	}

	cancelService() {
		this.appInsights.addStory(StoryType.ContractWizard, 'cancelService() called');
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.updateUserContract(u.uid, {
						cancelService: true,
						cancelDate: new Date().toISOString()
					})
				)
			)
		);
	}

	delete() {
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				zip(
					this.db.updateUser(u.uid, {
						contract: null,
						contractNo: null
					}),

					this.db.setSubscriptionRequired(u.uid, true),
					this.db.removeUserVehicles(u.uid)
				)
			)
		);
	}

	findConditionsPayment(): Observable<string> {
		return this.store.pipe(
			select(getAccessCodeData),
			withLatestFrom(
				this.store.pipe(
					select(getContractData),
					map((x: any) => (x && x.selectedService) || null)
				),
				this.store.pipe(select(getContractCreateSelectedPayment)),
				this.store.pipe(select(getLayoutScopeId)),
				this.store.pipe(select(getOrganizationEntities)),
				this.store.pipe(select(getContractDataGroupId))
			),
			switchMap(([accessCodeData, selectedServiceId, selectedPayment, scopeId, organizations, selectedGroupId]: any) => {
				const organization = (organizations || {})[scopeId];
				const isSelectableGroup = organization && organization.config && organization.config.enableSelectableGroup;
				let organizationData: any = {};

				if (accessCodeData) {
					organizationData = accessCodeData;
				}
				if (isSelectableGroup && organization.groups && organization.groups[selectedGroupId]) {
					organizationData = organization.groups[selectedGroupId];
				}

				if (selectedServiceId && organizationData && organizationData.services) {
					if (organizationData.services[selectedServiceId] && organizationData.meta && organizationData.meta.conditions) {
						switch (selectedPayment) {
							case 'Credit':
							case 'CreditLumpSum':
								if (organizationData.meta.conditions.paymentCc) {
									return of(organizationData.meta.conditions.paymentCc);
								}
								break;
							case 'Cash':
								if (organizationData.meta.conditions.paymentInternet) {
									return of(organizationData.meta.conditions.paymentInternet);
								}
								break;
							case 'PreAuthorized':
								if (organizationData.meta.conditions.paymentPpa) {
									return of(organizationData.meta.conditions.paymentPpa);
								}
								break;
							case 'DAS':
								if (organizationData.meta.conditions.paymentDas) {
									return of(organizationData.meta.conditions.paymentDas);
								}
								break;
							default:
								if (organizationData.meta.conditions.paymentInternet) {
									return of(organizationData.meta.conditions.paymentInternet);
								}
								break;
						}
					}
				}
				switch (selectedPayment) {
					case 'Credit':
					case 'CreditLumpSum':
						return of(this.conditionsText.paymentCreditCard);
					case 'Cash':
						return of(this.conditionsText.paymentInternet);
					case 'PreAuthorized':
						return of(this.conditionsText.paymentPpa);
					case 'DAS':
						return of(this.conditionsText.paymentDas);
					default:
						return of(this.conditionsText.paymentInternet);
				}
			})
		);
	}

	findConditionsService(): Observable<string> {
		return this.store.pipe(
			select(getAccessCodeData),
			withLatestFrom(
				this.store.pipe(
					select(getContractData),
					map((x: any) => (x && x.selectedService) || null)
				),
				this.store.pipe(select(getLayoutScopeId)),
				this.store.pipe(select(getOrganizationEntities)),
				this.store.pipe(select(getContractDataGroupId))
			),
			switchMap(([accessCodeData, selectedServiceId, scopeId, organizations, selectedGroupId]: any) => {
				const organization = (organizations || {})[scopeId];
				const isSelectableGroup = organization && organization.config && organization.config.enableSelectableGroup;
				let organizationData: any = {};

				if (accessCodeData) {
					organizationData = accessCodeData;
				}
				if (isSelectableGroup && organization.groups && organization.groups[selectedGroupId]) {
					organizationData = organization.groups[selectedGroupId];
				}
				if (selectedServiceId && organizationData && organizationData.services) {
					if (organizationData.services[selectedServiceId] && organizationData.meta && organizationData.meta.conditions) {
						if (organizationData.meta.conditions.service) {
							return of(organizationData.meta.conditions.service);
						}
					}
				}
				return of(this.conditionsText.service);
			})
		);
	}

	hydrate(): Observable<ContractInfo> {
		return this.auth.user.pipe(switchMap(u => this.db.userContract$<ContractInfo>(u.uid)));
	}

	setPaymentMode(mode: boolean): Observable<void> {
		this.appInsights.addStory(StoryType.ContractWizard, 'setPaymentMode() called', { mode });
		return this.auth.user.pipe(
			take(1),
			switchMap(u =>
				from(
					this.db.updateUserContract(u.uid, {
						payment: mode,
						conditionsPaymentAcceptedAt: null,
						paymentAcceptedAt: null
					})
				)
			)
		);
	}

	setCemRoles(uid: string, rolesJson: string) {
		return this.db.setCemRoles(uid, rolesJson);
	}

	capacitySystem$<T>() {
		return this.db.capacitySystem$<T>();
	}

	setContractMode(uid: string, value: any) {
		return this.db.setUserContractMode(uid, value);
	}

	clearUserAccessCode(uid: string) {
		return this.db.setUserAccessCode(uid, null);
	}

	userHost$(uid: string) {
		return this.db.userHost$(uid);
	}

	userContractServices$(uid: string): Observable<{ [key: string]: any }> {
		return this.db.userContractServices$(uid);
	}

	userContractAvailableServiceId$(uid: string) {
		return this.db.userContractAvailableServiceId$(uid);
	}

	userContractParkIdentificationNo$(uid: string) {
		return this.db.userContractParkIdentificationNo$(uid);
	}

	userContractSubscriptionService$(uid: string) {
		return this.db.userContractSubscriptionService$(uid);
	}

	userBooking$(uid: string) {
		return this.db.userBooking$(uid);
	}

	setUserBooking(uid: string, value: any) {
		return this.db.setUserBooking(uid, value);
	}

	userCalendarBlockedUntil$(uid: string) {
		return this.db.userCalendarBlockedUntil$(uid);
	}

	setUserCalendarBlockedUntil(uid: string, value: any) {
		return this.db.setUserCalendarBlockedUntil(uid, value);
	}
}
